import { sortBy } from "lodash-es";
import { ensureExhaustive } from "@dhau/lang-extras";
import type {
	BrickColourHexString,
	AutoPaletteMode,
	CustomPaletteMode,
	Palette,
	HybridPaletteMode,
	BuildBitmap,
	BrickColour,
	WritableBuildBitmap,
	PictureConfiguration,
} from "../model/index.ts";
import {
	getClosestPaletteBrick,
	getClosestPaletteItem,
} from "../model/index.ts";
import { bitmapChannels, cloneBitmapToWritable } from "../bitmap/index.ts";
import type {
	BuildOperationInput,
	BuildOperationResult,
	OperationBuildSources,
} from "./operation.ts";
import { readUInt32BE, writeUInt32BE } from "../utils/uint8-array.ts";
import compileMaskingProps from "./compile-masking-props.ts";
import { floatFloor } from "../utils/float.ts";
import { hslToRgb } from "../utils/colour-conversions.ts";

// Far from comprehensive, but unlikely to need more than this
const noBrickL = 0.5;
const noBrickS = 1;

function clamp255(v: number) {
	return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

function findAvailableNoBrickColour(systemPalette: Palette) {
	function findHue(
		potentialHue: number,
		attemptNumber: number,
	): BuildBitmap["noBrickColour"] {
		const rgb = hslToRgb(potentialHue, noBrickS, noBrickL);
		if (
			!systemPalette.some(
				(c) =>
					c.brick.r === rgb.r && c.brick.g === rgb.g && c.brick.b === rgb.b,
			)
		) {
			return {
				rgba: rgb.r * 0x1000000 + rgb.g * 0x10000 + rgb.b * 0x100 + 0xff,
			};
		}
		if (attemptNumber > 100) {
			throw new Error("Couldn't find a no-brick colour");
		}

		return findHue((potentialHue + 17) % 360, attemptNumber + 1);
	}
	// bright pink hue
	return findHue(310, 0);
}

function* applyPalette(
	image: WritableBuildBitmap,
	palette: Palette,
	config: PictureConfiguration,
	{ backgroundMask }: OperationBuildSources,
	systemPalette: Palette,
): Generator<undefined, Pick<BuildBitmap, "noBrickColour">> {
	// Note: don't use data.length, doesn't equal this
	// 4 bytes per pixel
	// Important to take a reference since it can change while generator running
	const { data, width, height } = image;
	const numPixelBytes = width * height * bitmapChannels;

	const maskingProps = compileMaskingProps(config, backgroundMask, image);

	let noBrickColour: BuildBitmap["noBrickColour"] = undefined;
	if (config.removeBackground && "noBrick" in config.removeBackground) {
		noBrickColour = findAvailableNoBrickColour(systemPalette);
	}

	let availablePalette = palette.filter(
		(p) => p.limit === undefined || p.limit > 0,
	);
	const usedCounts = new Map<Palette[number], number>();
	// Dithering-aware palette application
	const { dithering } = config;

	// Helper to update usage counts and handle palette limits
	function afterWrite(chosen: Palette[number]) {
		const colourCount = usedCounts.get(chosen);
		let newCount = 1;
		if (colourCount !== undefined) {
			newCount = colourCount + 1;
		}
		usedCounts.set(chosen, newCount);
		if (chosen.limit !== undefined && newCount >= chosen.limit) {
			availablePalette = availablePalette.filter((p) => p !== chosen);
		}
	}

	// Utility to test if mask marks a pixel as background (no-brick)
	function isMaskedBackground(x: number, y: number) {
		if (noBrickColour === undefined || maskingProps === undefined) {
			return false;
		}
		const maskX = floatFloor(
			(x + maskingProps.croppedXOffset) / maskingProps.usedResizeScale,
		);
		const maskY = floatFloor(
			(y + maskingProps.croppedYOffset) / maskingProps.usedResizeScale,
		);
		return maskingProps.foregroundRatio[maskX]?.[maskY] === 0;
	}

	// No dithering: preserve current behavior
	if (dithering === undefined) {
		for (let p = 0; p < numPixelBytes; p += bitmapChannels) {
			if (noBrickColour !== undefined && maskingProps !== undefined) {
				const pixelIndex = p / bitmapChannels;
				const x = pixelIndex % width;
				const y = (pixelIndex - x) / width;
				if (isMaskedBackground(x, y)) {
					writeUInt32BE(data, noBrickColour.rgba, p);
					continue;
				}
			}

			// Note: considered using a Uint32Array with data's buffer. Problem is that
			// Uint32Array reads out little endian (opposite to readUInt32BE). So have to
			// convert anyway.
			const pixel = readUInt32BE(data, p);

			// Note: getClosestPaletteItem inside has a cache
			const closest = getClosestPaletteItem(availablePalette, pixel);

			afterWrite(closest);
			writeUInt32BE(data, closest.brick.rgba, p);

			if (p % 1000 === 0) {
				yield;
			}
		}
		return { noBrickColour };
	}

	// Floyd–Steinberg error diffusion
	const serpentine = dithering.serpentine;
	const clampAmount = dithering.clamp * 255;
	const strength = dithering.strength;
	// Two rows of RGB errors (width * 3)
	let errCurr = new Float32Array(width * 3);
	let errNext = new Float32Array(width * 3);
	let processed = 0;

	for (let y = 0; y < height; y += 1) {
		const leftToRight = !serpentine || y % 2 === 0;
		for (let xi = 0; xi < width; xi += 1) {
			const x = leftToRight ? xi : width - 1 - xi;
			const p = (y * width + x) * bitmapChannels;
			if (
				noBrickColour !== undefined &&
				maskingProps !== undefined &&
				isMaskedBackground(x, y)
			) {
				// do not diffuse into/out of masked pixels
				writeUInt32BE(data, noBrickColour.rgba, p);
				// clear any accumulated error at this site
				const idx = x * 3;
				errCurr[idx + 0] = 0;
				errCurr[idx + 1] = 0;
				errCurr[idx + 2] = 0;
				continue;
			}

			const r = data[p];
			const g = data[p + 1];
			const b = data[p + 2];
			const idx = x * 3;
			// Apply accumulated error scaled by strength
			const rr = clamp255(r + errCurr[idx + 0] * strength);
			const gg = clamp255(g + errCurr[idx + 1] * strength);
			const bb = clamp255(b + errCurr[idx + 2] * strength);

			const adjusted =
				((rr & 0xff) << 24) | ((gg & 0xff) << 16) | ((bb & 0xff) << 8) | 0xff;
			const closest = getClosestPaletteItem(availablePalette, adjusted);
			afterWrite(closest);
			writeUInt32BE(data, closest.brick.rgba, p);

			// Compute quantization error (actual - quantized)
			let er = rr - closest.brick.r;
			let eg = gg - closest.brick.g;
			let eb = bb - closest.brick.b;
			// Clamp error per channel to reduce worming
			if (er > clampAmount) {
				er = clampAmount;
			}
			if (er < -clampAmount) {
				er = -clampAmount;
			}
			if (eg > clampAmount) {
				eg = clampAmount;
			}
			if (eg < -clampAmount) {
				eg = -clampAmount;
			}
			if (eb > clampAmount) {
				eb = clampAmount;
			}
			if (eb < -clampAmount) {
				eb = -clampAmount;
			}

			// Distribute error with FS weights
			// Weights: right 7/16, down 5/16, down-right 1/16, down-left 3/16
			if (leftToRight) {
				// right (same row)
				if (x + 1 < width && !isMaskedBackground(x + 1, y)) {
					const rIdx = (x + 1) * 3;
					errCurr[rIdx + 0] += er * (7 / 16);
					errCurr[rIdx + 1] += eg * (7 / 16);
					errCurr[rIdx + 2] += eb * (7 / 16);
				}
				// down-left
				if (y + 1 < height && x - 1 >= 0 && !isMaskedBackground(x - 1, y + 1)) {
					const dlIdx = (x - 1) * 3;
					errNext[dlIdx + 0] += er * (3 / 16);
					errNext[dlIdx + 1] += eg * (3 / 16);
					errNext[dlIdx + 2] += eb * (3 / 16);
				}
				// down
				if (y + 1 < height && !isMaskedBackground(x, y + 1)) {
					const dIdx = x * 3;
					errNext[dIdx + 0] += er * (5 / 16);
					errNext[dIdx + 1] += eg * (5 / 16);
					errNext[dIdx + 2] += eb * (5 / 16);
				}
				// down-right
				if (
					y + 1 < height &&
					x + 1 < width &&
					!isMaskedBackground(x + 1, y + 1)
				) {
					const drIdx = (x + 1) * 3;
					errNext[drIdx + 0] += er * (1 / 16);
					errNext[drIdx + 1] += eg * (1 / 16);
					errNext[drIdx + 2] += eb * (1 / 16);
				}
			} else {
				// left (same row)
				if (x - 1 >= 0 && !isMaskedBackground(x - 1, y)) {
					const lIdx = (x - 1) * 3;
					errCurr[lIdx + 0] += er * (7 / 16);
					errCurr[lIdx + 1] += eg * (7 / 16);
					errCurr[lIdx + 2] += eb * (7 / 16);
				}
				// down-right
				if (
					y + 1 < height &&
					x + 1 < width &&
					!isMaskedBackground(x + 1, y + 1)
				) {
					const drIdx = (x + 1) * 3;
					errNext[drIdx + 0] += er * (3 / 16);
					errNext[drIdx + 1] += eg * (3 / 16);
					errNext[drIdx + 2] += eb * (3 / 16);
				}
				// down
				if (y + 1 < height && !isMaskedBackground(x, y + 1)) {
					const dIdx = x * 3;
					errNext[dIdx + 0] += er * (5 / 16);
					errNext[dIdx + 1] += eg * (5 / 16);
					errNext[dIdx + 2] += eb * (5 / 16);
				}
				// down-left
				if (y + 1 < height && x - 1 >= 0 && !isMaskedBackground(x - 1, y + 1)) {
					const dlIdx = (x - 1) * 3;
					errNext[dlIdx + 0] += er * (1 / 16);
					errNext[dlIdx + 1] += eg * (1 / 16);
					errNext[dlIdx + 2] += eb * (1 / 16);
				}
			}

			// Clear the error we just consumed at this site
			errCurr[idx + 0] = 0;
			errCurr[idx + 1] = 0;
			errCurr[idx + 2] = 0;

			processed += 1;
			if (processed % 1000 === 0) {
				yield;
			}
		}
		// Move next row into current, and clear next
		const tmp = errCurr;
		errCurr = errNext;
		errNext = tmp;
		errNext.fill(0);
	}
	return { noBrickColour };
}

function* getBrickPrePenCounts(
	image: BuildBitmap,
	palette: Palette,
): Generator<undefined, Record<BrickColourHexString, number>> {
	const counts = Object.fromEntries(palette.map((c) => [c.brick.hexString, 0]));
	// Note: don't use data.length, doesn't equal this
	// 4 bytes per pixel
	// Note: important to take a reference since can change while generator running
	// TODO: Ignore no brick
	const { data, width, height } = image;
	const noBrickRgba = image.noBrickColour?.rgba;
	const numPixelBytes = width * height * bitmapChannels;
	for (let p = 0; p < numPixelBytes; p += bitmapChannels) {
		const pixel = readUInt32BE(data, p);
		if (pixel === noBrickRgba) {
			continue;
		}

		const closest = getClosestPaletteBrick(palette, pixel);
		counts[closest.hexString] += 1;
		if (p % 1000 === 0) {
			yield;
		}
	}
	return counts;
}

function* applyCustomPaletteMode(
	image: BuildBitmap,
	paletteMode: CustomPaletteMode,
	systemPalette: Palette,
	config: PictureConfiguration,
	sources: OperationBuildSources,
): Generator<undefined, BuildOperationResult> {
	const clonedImage = yield* cloneBitmapToWritable(image);
	const { noBrickColour } = yield* applyPalette(
		clonedImage,
		systemPalette.filter((p) =>
			paletteMode.palette.some((i) => i.hexString === p.brick.hexString),
		),
		config,
		sources,
		systemPalette,
	);
	return {
		type: "cloned",
		clonedImage: { ...clonedImage, noBrickColour },
	};
}

function* applyTopPalette(
	image: BuildBitmap,
	numberOfColours: number,
	systemPalette: Palette,
	config: PictureConfiguration,
	sources: OperationBuildSources,
	extraFixedColours: readonly BrickColour[] = [],
) {
	if (numberOfColours < extraFixedColours.length) {
		throw new Error("Can't have more fixed colours than total colours");
	}

	// Note: We specifically want to choose the palette before pen's impact.
	// This makes pen marks stable. i.e. if writing over some colour area the
	// image doesn't "jump around" because of changing palette snaps.
	const beforePenCounts = yield* getBrickPrePenCounts(image, systemPalette);

	const orderedBeforePenCounts = sortBy(
		Object.entries(beforePenCounts),
		([colour]) =>
			extraFixedColours.some((c) => c.hexString === colour) ? 0 : 1,
		([, count]) => -count,
		// This second condition is only to make sure the sort is stable
		([colour]) => colour,
	).map(([c]) => c);

	const topColours = new Set<BrickColourHexString>(
		orderedBeforePenCounts.slice(0, numberOfColours),
	);

	// TODO: This is a reference into a weakmap, so if could cache would improve that
	// The idea here is to be able to use the weak cache for closest palette calculations
	// In benchmarks, this second applyPalette takes about twice as long as the system
	// one above.
	const topPalette = systemPalette.filter(({ brick }) =>
		topColours.has(brick.hexString),
	);

	const clonedImage = yield* cloneBitmapToWritable(image);
	const { noBrickColour } = yield* applyPalette(
		clonedImage,
		topPalette,
		config,
		sources,
		systemPalette,
	);
	return {
		type: "cloned" as const,
		clonedImage: { ...clonedImage, noBrickColour },
	};
}

function* applyAutoPalette(
	image: BuildBitmap,
	paletteMode: AutoPaletteMode,
	systemPalette: Palette,
	config: PictureConfiguration,
	sources: OperationBuildSources,
) {
	return yield* applyTopPalette(
		image,
		paletteMode.numberOfColours,
		systemPalette,
		config,
		sources,
	);
}

// Note: this isn't a perfect algorithm and probably not even good. But it's good enough for now.
// Problem: Fixed palette colours interacting with non-fixed palette
function* applyHybridPalette(
	image: BuildBitmap,
	paletteMode: HybridPaletteMode,
	systemPalette: Palette,
	config: PictureConfiguration,
	sources: OperationBuildSources,
) {
	return yield* applyTopPalette(
		image,
		paletteMode.palette.length + paletteMode.numberOfExtraColours,
		systemPalette,
		config,
		sources,
		paletteMode.palette,
	);
}

function* paletteModeOperation({
	config,
	intermediate: { image },
	systemPalette,
	sources,
}: Pick<
	BuildOperationInput,
	"config" | "intermediate" | "systemPalette" | "sources"
>): Generator<undefined, BuildOperationResult> {
	switch (config.paletteMode.type) {
		case "custom":
			return yield* applyCustomPaletteMode(
				image,
				config.paletteMode,
				systemPalette,
				config,
				sources,
			);
		case "auto":
			return yield* applyAutoPalette(
				image,
				config.paletteMode,
				systemPalette,
				config,
				sources,
			);
		case "hybrid":
			return yield* applyHybridPalette(
				image,
				config.paletteMode,
				systemPalette,
				config,
				sources,
			);
		default:
			ensureExhaustive(config.paletteMode);
	}
}

export default paletteModeOperation;
